import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys, useAuthenticatedClient } from './queries'
import type { components } from './schema'

// Stock we hold in our own warehouse(s) (USA-1572).
export type Warehouse = components['schemas']['WarehouseRead']
export type WarehouseWrite = components['schemas']['WarehouseWrite']
export type WarehouseUpdate = components['schemas']['WarehouseUpdate']
export type WarehouseStockDetail = components['schemas']['WarehouseStockDetailRead']
// One receipt of a product in one place.
export type WarehouseLot = components['schemas']['WarehouseLotRead']
// What we hold of one product in one condition, across its places.
export type WarehouseItem = components['schemas']['WarehouseItemRead']
export type WarehouseItemDetail = components['schemas']['WarehouseItemDetailRead']
export type WarehouseStockEvent = components['schemas']['WarehouseStockEventRead']
export type WarehouseStockReceive = components['schemas']['WarehouseStockReceive']
export type WarehouseStockUpdate = components['schemas']['WarehouseStockUpdate']
export type QuickShipListing = components['schemas']['QuickShipListingRead']
export type StockCondition = WarehouseLot['condition']
export type UsedGrade = NonNullable<WarehouseLot['used_grade']>
export type StockPackaging = NonNullable<WarehouseStockDetail['packaging']>

export type StockListParams = {
  search?: string
  warehouse?: string
  condition?: StockCondition
  status?: 'active' | 'depleted' | 'removed' | 'all'
  product_uuid?: string
  page?: number
  limit?: number
}

export function useWarehouses() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.warehouse.warehouses,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/warehouse/warehouses')
      if (error) throw error
      return data
    },
  })
}

export function useItems(params: StockListParams) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.warehouse.items(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/warehouse/items', { params: { query: params } })
      if (error) throw error
      return data
    },
    placeholderData: keepPreviousData,
  })
}

export function useItem(productUuid: string, condition: StockCondition, grade: UsedGrade | null) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.warehouse.item(productUuid, condition, grade),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/warehouse/items/{product_uuid}', {
        params: { path: { product_uuid: productUuid }, query: { condition, used_grade: grade } },
      })
      if (error) throw error
      return data
    },
  })
}

export function useStock(uuid: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.warehouse.stock(uuid),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/warehouse/stock/{uuid}', {
        params: { path: { uuid } },
      })
      if (error) throw error
      return data
    },
    enabled: Boolean(uuid),
  })
}

export function useProductStock(productUuid: string | undefined) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.warehouse.productStock(productUuid ?? ''),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/warehouse/products/{product_uuid}/stock', {
        params: { path: { product_uuid: productUuid ?? '' } },
      })
      if (error) throw error
      return data
    },
    enabled: Boolean(productUuid),
  })
}

/** After any stock change: the lot itself from the server's answer, every list from a refetch. */
function useSettleStock() {
  const queryClient = useQueryClient()
  return (detail: WarehouseStockDetail | WarehouseItemDetail | undefined) => {
    if (detail && 'uuid' in detail) queryClient.setQueryData(queryKeys.warehouse.stock(detail.uuid), detail)
    void queryClient.invalidateQueries({ queryKey: queryKeys.warehouse.all })
  }
}

export function useReceiveStock() {
  const getClient = useAuthenticatedClient()
  const settle = useSettleStock()
  return useMutation({
    mutationFn: async (body: WarehouseStockReceive) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/warehouse/stock', { body })
      if (error) throw error
      return data
    },
    onSuccess: settle,
  })
}

export function useUpdateStock(uuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const settle = useSettleStock()
  const key = queryKeys.warehouse.stock(uuid)
  return useMutation({
    mutationFn: async (body: WarehouseStockUpdate) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/warehouse/stock/{uuid}', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
    // The field shows its new value at once; a refused change puts the old one back.
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<WarehouseStockDetail>(key)
      if (previous) {
        queryClient.setQueryData<WarehouseStockDetail>(key, {
          ...previous,
          ...body,
          condition: body.condition ?? previous.condition,
          unit_net: 'unit_net' in body ? (body.unit_net == null ? null : String(body.unit_net)) : previous.unit_net,
        })
      }
      return { previous }
    },
    onError: (_error, _body, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    onSuccess: settle,
  })
}

export function useAdjustStock(uuid: string) {
  const getClient = useAuthenticatedClient()
  const settle = useSettleStock()
  return useMutation({
    mutationFn: async (body: { quantity_delta: number; note: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/warehouse/stock/{uuid}/adjust', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: settle,
  })
}

export function useRemoveStock(uuid: string) {
  const getClient = useAuthenticatedClient()
  const settle = useSettleStock()
  return useMutation({
    mutationFn: async (note: string) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/warehouse/stock/{uuid}/remove', {
        params: { path: { uuid } },
        body: { note },
      })
      if (error) throw error
      return data
    },
    onSuccess: settle,
  })
}

export function useMoveStock(uuid: string) {
  const getClient = useAuthenticatedClient()
  const settle = useSettleStock()
  return useMutation({
    mutationFn: async (body: {
      bay: string | null
      pallet: string | null
      warehouse_uuid?: string | null
      /** Fewer than the lot holds moves only that many, as a new lot. */
      quantity?: number
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/warehouse/stock/{uuid}/move', {
        params: { path: { uuid } },
        body,
      })
      if (error) throw error
      return data
    },
    onSuccess: settle,
  })
}

export function useSaveWarehouse() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      input: { uuid: string; body: WarehouseUpdate } | { uuid?: undefined; body: WarehouseWrite },
    ) => {
      const client = await getClient()
      const { data, error } = input.uuid
        ? await client.PATCH('/dashboard/warehouse/warehouses/{uuid}', {
            params: { path: { uuid: input.uuid } },
            body: input.body,
          })
        : await client.POST('/dashboard/warehouse/warehouses', { body: input.body as WarehouseWrite })
      if (error) throw error
      return data
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.warehouse.warehouses })
    },
  })
}

/** Staff's price for a Quick Ship listing; null sells at the suggested price again. The store follows. */
export function useSaveListingPrice(listingUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (price: string | null) => {
      const client = await getClient()
      const { data, error } = await client.PATCH('/dashboard/warehouse/listings/{listing_uuid}', {
        params: { path: { listing_uuid: listingUuid } },
        body: { price },
      })
      if (error) throw error
      return data
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.warehouse.all })
    },
  })
}

/** Push a listing to the store now (after a failed push). */
export function usePushListing(listingUuid: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/warehouse/listings/{listing_uuid}/sync', {
        params: { path: { listing_uuid: listingUuid } },
      })
      if (error) throw error
      return data
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.warehouse.all })
    },
  })
}
